import { useState, useEffect } from 'react';
import { OpenInNew, InfoOutlined, CommentOutlined } from '@mui/icons-material';
import {
  TabDialog,
  DrawerTabProps,
  TabDialogProps,
  TabDrawerProps,
  selectUser,
} from '@centerpiece/components';
import { Badge, IconButton, Tooltip, useTheme } from '@mui/material';
import { Event, ItemStatus, ITEMSTATUSES, Comment } from 'store/api/models';
import { getItemStatus, getStatusColorFromDate } from 'utils/status.utils';
import { ItemInfoTab } from 'drawers/tabs/ItemInfoTab';
import {
  ItemProvider,
  ItemContext,
  ItemContextContent,
} from 'contexts/item.context';
import { CommentsTab } from 'drawers/tabs/CommentsTab';

export interface ItemPreviewDialogProps
  extends Omit<TabDialogProps, 'tabs' | 'setTabs' | 'title' | 'status'> {
  id: number;
  project_id: number;
  transparentBackdrop?: boolean;
  createProject?: Function;
  afterSave?: Function;
  expand?: Function;
  template?: boolean;
  disabled?: boolean;
  breadcrumbs?: { label: string; onClick?: Function }[];
  subType?: string[];
}

export const ItemPreviewDialog = (props: ItemPreviewDialogProps) => {
  return (
    <ItemProvider {...props}>
      <ItemContext.Consumer>
        {contextProps => <DialogComponent {...props} {...contextProps} />}
      </ItemContext.Consumer>
    </ItemProvider>
  );
};

const DialogComponent = (
  props: ItemPreviewDialogProps & ItemContextContent
) => {
  const {
    id,
    project_id,
    itemLoading,
    title,
    status,
    afterSave = () => {},
    expand = () => {},
    numChats,
    due_date,
    comments,
    relationships,
  } = props;
  const theme = useTheme();

  return (
    <TabDialog
      {...props}
      width={{ xs: '100%' }}
      maxWidth={theme.breakpoints.values.md}
      isLoading={itemLoading}
      contentLoading={itemLoading}
      status={getItemStatus({
        status: status,
        statuses: [],
        disabled: true,
        color:
          status.id === ITEMSTATUSES.COMPLETE
            ? 'complete'
            : due_date
            ? getStatusColorFromDate(due_date)[0]
            : 'inherit',
        onClick: (sts: any) => {},
      })}
      transparentBackdrop={!!props.transparentBackdrop}
      title={title}
      icons={[
        <Tooltip title={'Expand item drawer'}>
          <IconButton
            color="primary"
            onClick={async () => {
              await expand();
            }}
          >
            <OpenInNew />
          </IconButton>
        </Tooltip>,
      ]}
      tabs={[
        {
          key: 'info',
          sequence: 1,
          visible: true,
          icon: <InfoOutlined />,
          label: 'Information',
          contents: <ItemInfoTab />,
        },
        {
          key: 'comments',
          sequence: 3,
          visible: true,
          icon: (
            <Badge badgeContent={numChats} color="secondary">
              <CommentOutlined />
            </Badge>
          ),
          label: 'Comments',
          padding: true,
          contents: (
            <CommentsTab
              comments={comments}
              recipients={relationships}
              afterSave={afterSave}
              foreignKey={{ item: id }}
            />
          ),
        },
      ]}
    />
  );
};
